import { useState, useEffect } from 'react';
import { useCurrency } from '../../../hooks/useCurrency';
import { Package, AlertTriangle, TrendingDown, Search, Filter, Calendar, Download } from 'lucide-react';
import { stockService } from '../api/stock';
import { medicineService } from '../api/medicines';
import { createRackService } from '../api/supabaseAdapter';
import type { StockSummary, BatchWithMedicine, Medicine } from '../types';
import { exportToCSV } from '../../../lib/csvExport';
import { checkAndCreatePharmacyAlerts } from '../services/notificationService';

export default function Stock() {
  const { formatCurrency } = useCurrency();
  const [stockSummary, setStockSummary] = useState<StockSummary[]>([]);
  const [batchesWithMedicine, setBatchesWithMedicine] = useState<BatchWithMedicine[]>([]);
  const [, setMedicines] = useState<Medicine[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<'summary' | 'batches'>('summary');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'low-stock' | 'near-expiry'>('all');
  const [racks, setRacks] = useState<any[]>([]);
  const [rackFilter, setRackFilter] = useState<string>('all');

  useEffect(() => {
    loadStockData();
  }, []);

  const loadStockData = async () => {
    try {
      const rackService = createRackService();
      const [summary, batches, medicinesData, racksData] = await Promise.all([
        stockService.getSummary(),
        stockService.getBatchesWithMedicine(),
        medicineService.getAll(),
        rackService.getAll()
      ]);
      
      setStockSummary(summary);
      setBatchesWithMedicine(batches);
      setMedicines(medicinesData);
      setRacks(racksData);
      
      if (summary.length > 0) {
        checkAndCreatePharmacyAlerts(summary).catch((err: unknown) => 
          console.warn('Failed to create pharmacy alerts:', err)
        );
      }
    } catch (error) {
      console.error('Failed to load stock data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = () => {
    if (view === 'summary') {
      exportToCSV(
        filteredSummary,
        'stock_summary',
        [
          { key: 'medicineName', header: 'Medicine Name' },
          { key: 'totalStock', header: 'Total Stock' },
          { key: 'reorderLevel', header: 'Reorder Level' },
          { key: 'nearestExpiry', header: 'Nearest Expiry' },
          { key: 'isLowStock', header: 'Low Stock' },
          { key: 'isNearExpiry', header: 'Near Expiry' }
        ]
      );
    } else {
      exportToCSV(
        filteredBatches,
        'batch_stock',
        [
          { key: 'medicine.name', header: 'Medicine Name' },
          { key: 'batchNo', header: 'Batch Number' },
          { key: 'quantityOnHand', header: 'Quantity' },
          { key: 'expiryDate', header: 'Expiry Date' },
          { key: 'location', header: 'Location' },
          { key: 'unitPrice', header: 'Unit Price' },
          { key: 'mrp', header: 'MRP' }
        ]
      );
    }
  };

  const filteredSummary = stockSummary.filter(item => {
    const matchesSearch = searchQuery === '' || 
      item.medicineName.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesFilter = filterType === 'all' || 
      (filterType === 'low-stock' && item.isLowStock) ||
      (filterType === 'near-expiry' && item.isNearExpiry);
    
    return matchesSearch && matchesFilter;
  });

  const filteredBatches = batchesWithMedicine.filter(batch => {
    const matchesSearch = searchQuery === '' || 
      batch.medicine.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      batch.batchNo.toLowerCase().includes(searchQuery.toLowerCase());
    
    const now = new Date();
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
    const expiryDate = new Date(batch.expiryDate);
    
    const matchesFilter = filterType === 'all' || 
      (filterType === 'low-stock' && batch.quantityOnHand <= batch.medicine.reorderLevel) ||
      (filterType === 'near-expiry' && expiryDate <= thirtyDaysFromNow && expiryDate > now);
    
    return matchesSearch && matchesFilter;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <section className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Stock Management</h1>
          <p className="text-sm text-neutral-600">Monitor medicine inventory and batch information</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleExportCSV}
            className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </button>
          <button
            onClick={() => setView('summary')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              view === 'summary'
                ? 'bg-blue-600 text-white'
                : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
            }`}
          >
            Summary View
          </button>
          <button
            onClick={() => setView('batches')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              view === 'batches'
                ? 'bg-blue-600 text-white'
                : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
            }`}
          >
            Batch View
          </button>
        </div>
      </header>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg border p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-neutral-400" />
              <input
                type="text"
                placeholder="Search medicines or batches..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-neutral-400" />
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value as 'all' | 'low-stock' | 'near-expiry')}
              className="px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Items</option>
              <option value="low-stock">Low Stock</option>
              <option value="near-expiry">Near Expiry</option>
            </select>
            {racks.length > 0 && (
              <select
                value={rackFilter}
                onChange={(e) => setRackFilter(e.target.value)}
                className="px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Racks</option>
                {racks.map(rack => (
                  <option key={rack.id} value={rack.id}>{rack.name}</option>
                ))}
              </select>
            )}
          </div>
        </div>
      </div>

      {/* Summary View */}
      {view === 'summary' && (
        <div className="bg-white rounded-lg border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-neutral-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Medicine
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Total Stock
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Reorder Level
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Nearest Expiry
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-neutral-200">
                {filteredSummary.map((item) => (
                  <tr key={item.medicineId} className="hover:bg-neutral-50">
                    <td className="px-6 py-4">
                      <div className="font-medium text-neutral-900">{item.medicineName}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`font-medium ${
                        item.isLowStock ? 'text-red-600' : 'text-neutral-900'
                      }`}>
                        {item.totalStock}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-neutral-900">{item.reorderLevel}</td>
                    <td className="px-6 py-4">
                      {item.nearestExpiry ? (
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 text-neutral-400 mr-2" />
                          <span className={`text-sm ${
                            item.isNearExpiry ? 'text-amber-600 font-medium' : 'text-neutral-900'
                          }`}>
                            {new Date(item.nearestExpiry).toLocaleDateString()}
                          </span>
                        </div>
                      ) : (
                        <span className="text-sm text-neutral-500">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        {item.isLowStock && (
                          <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">
                            <TrendingDown className="h-3 w-3 mr-1" />
                            Low Stock
                          </span>
                        )}
                        {item.isNearExpiry && (
                          <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-amber-100 text-amber-800 rounded-full">
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            Near Expiry
                          </span>
                        )}
                        {!item.isLowStock && !item.isNearExpiry && (
                          <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                            <Package className="h-3 w-3 mr-1" />
                            Good
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {filteredSummary.length === 0 && (
              <div className="text-center py-8">
                <p className="text-neutral-500">No stock items found</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Batch View */}
      {view === 'batches' && (
        <div className="bg-white rounded-lg border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-neutral-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Medicine
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Batch No
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Quantity
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Expiry Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Location
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Unit Price
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    MRP
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-neutral-200">
                {filteredBatches.map((batch) => {
                  const now = new Date();
                  const thirtyDaysFromNow = new Date();
                  thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
                  const expiryDate = new Date(batch.expiryDate);
                  const isNearExpiry = expiryDate <= thirtyDaysFromNow && expiryDate > now;
                  const isExpired = expiryDate <= now;
                  const isLowStock = batch.quantityOnHand <= batch.medicine.reorderLevel;

                  return (
                    <tr key={batch.id} className={`hover:bg-neutral-50 ${
                      isExpired ? 'bg-red-50' : isNearExpiry ? 'bg-amber-50' : ''
                    }`}>
                      <td className="px-6 py-4">
                        <div className="font-medium text-neutral-900">{batch.medicine.name}</div>
                        <div className="text-sm text-neutral-500">{batch.medicine.strength}</div>
                      </td>
                      <td className="px-6 py-4 text-sm text-neutral-900 font-mono">{batch.batchNo}</td>
                      <td className="px-6 py-4">
                        <span className={`font-medium ${
                          isLowStock ? 'text-red-600' : 'text-neutral-900'
                        }`}>
                          {batch.quantityOnHand}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`text-sm ${
                          isExpired ? 'text-red-600 font-medium' :
                          isNearExpiry ? 'text-amber-600 font-medium' : 'text-neutral-900'
                        }`}>
                          {expiryDate.toLocaleDateString()}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-neutral-900 capitalize">
                        {batch.location || 'main'}
                      </td>
                      <td className="px-6 py-4 text-sm text-neutral-900">
                        ${batch.unitPrice.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 text-sm text-neutral-900">
                        {batch.mrp ? `$${batch.mrp.toFixed(2)}` : '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            
            {filteredBatches.length === 0 && (
              <div className="text-center py-8">
                <p className="text-neutral-500">No batches found</p>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
